import type { TutorialBereich, TutorialKapitel, TutorialKapitelId } from '../../tutorial/types';

/**
 * LERNZENTRUM — die Regeln ohne Datenbank.
 *
 * Alles, was das Lernzentrum RECHNET, steht hier: Fortschritt je Bereich, der
 * Lernpfad mit Fortschritt, die Suche. Die Tests laufen ohne jsdom und ohne
 * Supabase; deshalb nimmt jede Funktion ihre Daten als Argument und gibt ein
 * neues Ergebnis zurueck — kein Zugriff auf Modulzustand, keine Zeitquelle
 * ausser der uebergebenen.
 */

/**
 * Was das Lernzentrum ueber ein Kapitel weiss. Strukturgleich mit
 * `Fortschritt` aus `tutorialService.ts`, aber hier eigenstaendig erklaert:
 * so bleibt diese Datei ohne jede Abhaengigkeit auf den Datenzugriff testbar.
 */
export interface FortschrittStand {
    gesehenAm: string | null;
    abgeschlossenAm: string | null;
}

export type FortschrittKarte = ReadonlyMap<TutorialKapitelId, FortschrittStand>;

/** Beschriftung der Bereichskacheln (Plan §2.2). */
export const BEREICH_LABEL: Record<TutorialBereich, string> = {
    einstieg: 'Einstieg',
    projekt: 'Projekte',
    begehung: 'Begehung',
    sigeplan: 'SiGe-Plan',
    einweisung: 'Einweisung',
    planung: 'Planungsphase',
    vorank: 'Vorankündigung',
    unterlage: 'Unterlage',
    asplan: 'A+S-Plan',
    bauordnung: 'Baustellenordnung',
    sifa: 'SiFa-Betreuung',
    crm: 'Kunden',
    angebot: 'Angebote',
    freelancer: 'Freelancer',
    rechnung: 'Rechnungen',
    buchhaltung: 'Buchhaltung',
    erp: 'Steuerung',
    admin: 'Verwaltung',
};

/** Ein Kapitel gilt als angefangen, sobald es einmal geoeffnet wurde. */
export const istGesehen = (stand?: FortschrittStand): boolean => !!stand && (!!stand.gesehenAm || !!stand.abgeschlossenAm);

export const istAbgeschlossen = (stand?: FortschrittStand): boolean => !!stand?.abgeschlossenAm;

// ─── Fortschritt ─────────────────────────────────────────────────────────────

export interface Fortschrittszahl {
    gesamt: number;
    gesehen: number;
    abgeschlossen: number;
    /** Anteil ABGESCHLOSSENER Kapitel in ganzen Prozent, 0 bei leerer Liste. */
    prozent: number;
}

/**
 * Gezaehlt wird nach ABGESCHLOSSEN, nicht nach gesehen.
 *
 * Grund: „gesehen" heisst nur, dass jemand die Seite einmal offen hatte — auch
 * ein Fehlklick setzt die Marke. Ein Balken, der davon auf 80 % springt, sagt
 * dem Nutzer etwas Falsches ueber seinen eigenen Stand. `gesehen` bleibt als
 * eigene Zahl daneben stehen und faerbt im Raster den halben Punkt.
 */
export const zaehleFortschritt = (kapitel: readonly TutorialKapitel[], fortschritt: FortschrittKarte): Fortschrittszahl => {
    let gesehen = 0;
    let abgeschlossen = 0;
    kapitel.forEach((k) => {
        const stand = fortschritt.get(k.id);
        if (istAbgeschlossen(stand)) abgeschlossen += 1;
        if (istGesehen(stand)) gesehen += 1;
    });
    const gesamt = kapitel.length;
    return { gesamt, gesehen, abgeschlossen, prozent: gesamt === 0 ? 0 : Math.round((abgeschlossen / gesamt) * 100) };
};

export interface Bereichsstand extends Fortschrittszahl {
    bereich: TutorialBereich;
    label: string;
    /** Gesamtdauer der Kapitel dieses Bereichs in Minuten. */
    dauerMin: number;
}

/**
 * Fortschritt je Bereich, in der Reihenfolge des ERSTEN Auftretens im Register.
 *
 * Nicht alphabetisch und nicht nach der Typ-Union: die Registerreihenfolge ist
 * die gewollte Lernreihenfolge (Einstieg zuerst, Verwaltung zuletzt), und eine
 * Kachelwand, die sich nach einem Kapitel-Nachtrag umsortiert, verliert den
 * Nutzer, der sich die Position gemerkt hat.
 *
 * Bereiche ohne sichtbares Kapitel kommen NICHT vor — eine leere Kachel „0 von
 * 0" ist kein Angebot, sondern eine Sackgasse.
 */
export const fortschrittJeBereich = (kapitel: readonly TutorialKapitel[], fortschritt: FortschrittKarte): Bereichsstand[] => {
    const reihenfolge: TutorialBereich[] = [];
    const gruppen = new Map<TutorialBereich, TutorialKapitel[]>();
    kapitel.forEach((k) => {
        const vorhanden = gruppen.get(k.bereich);
        if (vorhanden) {
            vorhanden.push(k);
        } else {
            gruppen.set(k.bereich, [k]);
            reihenfolge.push(k.bereich);
        }
    });
    return reihenfolge.map((bereich) => {
        const liste = gruppen.get(bereich) ?? [];
        return {
            bereich,
            label: BEREICH_LABEL[bereich] ?? bereich,
            dauerMin: liste.reduce((s, k) => s + (Number.isFinite(k.dauerMin) ? k.dauerMin : 0), 0),
            ...zaehleFortschritt(liste, fortschritt),
        };
    });
};

// ─── Lernpfad ────────────────────────────────────────────────────────────────

export interface LernpfadEintrag {
    kapitel: TutorialKapitel;
    /** Platz im Pfad, ab 1 — die Nummer, die auf dem Bildschirm steht. */
    nummer: number;
    gesehen: boolean;
    abgeschlossen: boolean;
    /**
     * Genau EIN Eintrag im Pfad traegt `istNaechstes`: das erste noch nicht
     * abgeschlossene Kapitel. Der Knopf „Weiterlernen" zeigt auf ihn.
     */
    istNaechstes: boolean;
}

/**
 * Der Lernpfad (topologisch sortiert, kommt so aus `lernpfad()` im Register)
 * mit dem Fortschritt des Nutzers daran.
 *
 * Die Reihenfolge wird NICHT nach Fortschritt umsortiert. Ein Pfad, der
 * abgeschlossene Kapitel nach unten schiebt, waere beim zweiten Besuch ein
 * anderer Pfad — und die Voraussetzungen stimmten nicht mehr.
 */
export const lernpfadMitFortschritt = (pfad: readonly TutorialKapitel[], fortschritt: FortschrittKarte): LernpfadEintrag[] => {
    let naechstesVergeben = false;
    return pfad.map((kapitel, i) => {
        const stand = fortschritt.get(kapitel.id);
        const abgeschlossen = istAbgeschlossen(stand);
        const istNaechstes = !abgeschlossen && !naechstesVergeben;
        if (istNaechstes) naechstesVergeben = true;
        return {
            kapitel,
            nummer: i + 1,
            gesehen: istGesehen(stand),
            abgeschlossen,
            istNaechstes,
        };
    });
};

/** Das erste nicht abgeschlossene Kapitel des Pfads, oder undefined wenn alles fertig ist. */
export const naechstesKapitel = (pfad: readonly TutorialKapitel[], fortschritt: FortschrittKarte): TutorialKapitel | undefined =>
    pfad.find((k) => !istAbgeschlossen(fortschritt.get(k.id)));

// ─── Suche ───────────────────────────────────────────────────────────────────

/**
 * Umlaute und scharfes S werden ausgeschrieben, damit „Vorankundigung",
 * „Maengel" und „Massnahme" dieselben Treffer liefern wie die korrekte
 * Schreibweise. Wer in der Hilfe nach etwas sucht, kennt die Schreibweise des
 * Fachworts oft gerade NICHT — das ist ja der Grund fuer die Suche.
 */
const falte = (s: string): string =>
    s
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss');

/**
 * Der durchsuchbare Text eines Kapitels: Titel, die Stichworte aller Schritte
 * und die Stolperfallen (aus dem Register und aus der kuratierten
 * Markdown-Datei). Praxisrahmen kommt mit, weil dort die Fachbegriffe stehen,
 * nach denen im Betrieb gefragt wird („Seitenschutz", „Kita").
 */
const suchtext = (k: TutorialKapitel, stolperfallen?: ReadonlyMap<TutorialKapitelId, string[]>): string =>
    falte(
        [
            k.titel,
            k.id,
            ...k.schritte.map((s) => s.stichwort),
            k.praxis.kontext,
            k.praxis.ziel,
            ...(k.stolperfallen ?? []),
            ...(stolperfallen?.get(k.id) ?? []),
        ].join('   '),
    );

/**
 * Suche ueber Titel, Stichworte und Stolperfallen. Clientseitig — 20 Kapitel
 * sind zu wenig fuer einen Serverlauf, und die Suche muss auch funktionieren,
 * wenn die Tabelle noch gar nicht existiert.
 *
 * Mehrere Woerter werden UND-verknuepft und duerfen in beliebiger Reihenfolge
 * an beliebigen Stellen stehen: „foto mangel" findet „Mangel mit Foto".
 * Reihenfolge der Treffer = Registerreihenfolge; eine Relevanzsortierung
 * ueber 20 Eintraege taeuscht Genauigkeit vor, die es nicht gibt.
 */
export const sucheKapitel = (
    kapitel: readonly TutorialKapitel[],
    suchbegriff: string,
    stolperfallen?: ReadonlyMap<TutorialKapitelId, string[]>,
): TutorialKapitel[] => {
    const worte = falte(suchbegriff ?? '').split(/\s+/).filter(Boolean);
    if (worte.length === 0) return [...kapitel];
    return kapitel.filter((k) => {
        const text = suchtext(k, stolperfallen);
        return worte.every((w) => text.includes(w));
    });
};
